
import React, { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, Button, Link, StepIcon, Typography } from "@mui/material";
import Divider from '@mui/material/Divider';
import Step from '@mui/material/Step';
import StepConnector, { stepConnectorClasses } from '@mui/material/StepConnector';
import Stepper from '@mui/material/Stepper';
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';
import Grid from '@mui/material/Unstable_Grid2'; // Grid version 2
import { ThemeProvider, styled, useTheme } from "@mui/material/styles";
import { AuthContext, Card, IUser, Ico_ArrowUp_Composed } from '@sagecentral/core-ui';
import { GridMetaData } from "models/grids/GridMetaData";
import { IProject, IProjectOverview } from "models/projects";
import { IMilestone } from "models/projects/milestones/Milestone";
import ProjectsService from "services/projects/projects-service";
import { formatDate } from "utils/date-utils";

import useProjectMilestones from "hooks/projects/useProjectMilestones";
import { useProjectPermissions } from "hooks/projects";
import { useFeatureEnabled } from "hooks/shared";

const KeyGrid = styled(Grid)({
    fontWeight: "bold"
});

const Milestones = () => {
    const auth = useContext(AuthContext);
    const permissions = useProjectPermissions();
    const theme = useTheme();
    const [gridMetaData, setGridMetaData] = useState<GridMetaData>({
        page: 1,
        pageSize: 15,
        sort: [{ field: "Id", dir: "asc" }]
    });
    const [user, setUser] = useState<IUser>({} as IUser);
    const [activeStep, setActiveStep] = React.useState(0);
    const [popoverOpen, setPopoverOpen] = React.useState(true);
    const [ wave4Enabled ] = useFeatureEnabled('wave-4');

    const [project, setProject] = useState<IProject | IProjectOverview>();
    
    const { projectId } = useParams();

    useEffect(() => {
        const readUser = async () => {
            const user = await auth.getUser() as IUser;
            setUser(user);
        };

        readUser();
    }, []);

    useEffect(() => {
        (async () => {
            if (user && user.id_token && user.id_token.length > 0) {
                const project = await ProjectsService.getProjectOverview(projectId, user.id_token);
                setProject(project);
            }
        })();
    }, [user]);

    const [milestones, total, loading, error] = useProjectMilestones(projectId, gridMetaData);

    const GetColorByStatus = (milestone: IMilestone) => {
        const status = milestone.taskStatus;
        let bgColor: string = theme.palette.grey[500];

        if (status == "Completed") {
            bgColor = theme.palette.grey[500];
        }
        else if (status == "Breached") {
            bgColor = theme.palette.error.main;
        }
        else if (status == "On Schedule") {
            bgColor = theme.palette.success.main;
        }
        return bgColor;
    }

    const StyledTooltip = styled(({ className, ...props }: TooltipProps) => (
        <Tooltip {...props} arrow classes={{ popper: className }} />
    ))(({ theme }) => ({
        [`& .${tooltipClasses.arrow}`]: {
            fontSize: 25,
            color: 'white',
            '&::before': {
                border: '1px solid #0000001f'
            }
        },
        [`& .${tooltipClasses.tooltip}`]: {
            backgroundColor: theme.palette.common.white,
            color: 'rgba(0, 0, 0, 0.87)',
            boxShadow: theme.shadows[3],
            fontSize: 16,
        },
    }));

    const GridValue = (value: any) => {
        return <Grid sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{value}</Grid>;
    }

    const GridLinkValue = (value: any, href: string) => {
        return <Grid sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}><Link target="_self" href={href}>{value}</Link></Grid>;
    }

    const GridValueWithTooltip = (value: any) => {
        return (
            <Tooltip title={value}>
                <Grid sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{value}</Grid>
            </Tooltip>
        )
    }

    const GridLinkValueWithTooltip = (value: any, href: string) => {
        return (
            <Tooltip title={value}>
                <Grid sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}><Link target="_self" href={href}>{value}</Link></Grid>
            </Tooltip>
        )
    }

    const CardContent = (milestone: IMilestone) => {
        let color: string = GetColorByStatus(milestone);
        return (
            <>
                <Box sx={{ flexGrow: 1, width: "900px" }}>
                    <Grid container spacing={2}>
                        <Grid xs={8}>
                            <Box>
                                <Grid sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", paddingLeft: "0px" }}>{milestone?.name}</Grid>
                                <Grid container spacing={1}>
                                    <KeyGrid xs={6}>WI ID:</KeyGrid>
                                    {GridValue(milestone?.workItemId)}
                                </Grid>
                                <Grid container spacing={1}>
                                    <KeyGrid xs={6}>End Date:</KeyGrid>
                                    {GridValue(formatDate(milestone?.endDate?.toString()))}
                                </Grid>
                                <Grid container spacing={1}>
                                    <KeyGrid xs={6}>Work Item:</KeyGrid>
                                    {GridLinkValueWithTooltip(milestone?.workItemName, process.env.REACT_APP_PUBLIC_URL + `/workitems/${milestone.workItemId}/overview`)}
                                </Grid>
                                <Grid container spacing={1}>
                                    <KeyGrid xs={6}>Workflow:</KeyGrid>
                                    {permissions?.canEditWorkflows
                                        ? GridLinkValue(milestone?.workflowName, process.env.REACT_APP_API_URL + `/WorkflowInstance/EditWorkflow?workflowInstanceID=${milestone.workflowId}`)
                                        : GridValue(milestone?.workflowName)
                                    }
                                </Grid>
                                <Grid container spacing={1}>
                                    <KeyGrid xs={6}>Responsibility:</KeyGrid>
                                    {GridValue(milestone?.responsibility)}
                                </Grid>
                                <Grid container spacing={1}>
                                    <KeyGrid xs={6}>Resource:</KeyGrid>
                                    {GridValue(milestone?.userName)}
                                </Grid>
                                <Grid container spacing={1}>
                                    <KeyGrid xs={6}>Status:</KeyGrid>
                                    <Grid sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: color }}>{milestone?.taskStatus}</Grid>
                                </Grid>
                            </Box>
                        </Grid>
                        <Divider orientation="vertical" flexItem />
                        <Grid sx={{ textAlign: "center", fontWeight: 600, alignSelf: "center" }}>
                            <>{formatDate(milestone?.milestoneDate?.toString())}</>
                        </Grid>
                    </Grid>
                </Box>
            </>
        )
    };

    const iconStep = (milestone: IMilestone) => {

        let bgColor: string = GetColorByStatus(milestone);
        return (
            milestone.taskStatus == 'Breached' ?
                <StyledTooltip open={popoverOpen}
                sx={{zIndex: 1}}
                title={
                    <React.Fragment>
                        <Box sx={{ color: 'red', padding: '16px' }}>Milestone date breached.</Box>
                    </React.Fragment>
                } placement="right">
                    <Box sx={{
                        backgroundColor: bgColor,
                        zIndex: 1,
                        color: '#fff',
                        width: 24,
                        height: 24,
                        display: 'flex',
                        borderRadius: '50%',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}></Box>
                </StyledTooltip> :
                <Box sx={{
                    backgroundColor: bgColor,
                    zIndex: 1,
                    color: '#fff',
                    width: 24,
                    height: 24,
                    display: 'flex',
                    borderRadius: '50%',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}></Box>
        )
    }

    const QontoConnector = styled(StepConnector)(({ theme }) => ({
        [`& .${stepConnectorClasses.line}`]: {
            borderColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#eaeaf0',
            borderTopWidth: 3,
            borderRadius: 1,
            height: "100%",
        },
    }));
    return (
        <ThemeProvider theme={useTheme()}>
            <Box sx={{ flexGrow: 1, width: "900px", padding: '25px 0 8px 12px' }}>
                <Grid container spacing={2}>
                    <KeyGrid xs={8}>
                        Task
                    </KeyGrid>
                    <KeyGrid sx={{ textAlign: "center", alignSelf: "center", ml: '34px' }}>
                        Milestone Date
                    </KeyGrid>
                </Grid>
            </Box>
            <Box>
                {milestones && milestones.length > 0 ? <>
                    <Box sx={{ display: "flex" }}>
                        <Box>
                            {milestones?.map((m, index) => {
                                return (
                                    <Box sx={{ gap: "8px" }}>
                                        <Card cardStyle={{ minWidth: "900px", borderRadius: "4px", boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.3)", border: theme.palette.grey[500] }} key={index} cardContent={CardContent(m)} />
                                    </Box>
                                )
                            })}
                        </Box>
                        <Stepper activeStep={activeStep} orientation="vertical" nonLinear={true} sx={{ margin: "110px 20px 120px" }} connector={<QontoConnector />} >
                            {milestones?.map((milestone, index) => (
                                <Step key={milestone.id}>
                                    <StepIcon icon={iconStep(milestone)}></StepIcon>
                                </Step>
                            ))}
                        </Stepper>
                    </Box>
                    <Box sx={{ display: 'flex' }}>
                        <Typography variant="button" color="secondary" sx={{ textTransform: 'none', mr: '12px', mt: '7px', ml: '2px', textDecoration: 'underline', cursor: 'pointer', display: 'flex' }} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>Back to Top <Box sx={{ mt: '2px', ml: '2px' }}><Ico_ArrowUp_Composed /></Box></Typography>
                        {milestones.length != total &&
                            <Button variant="contained" color="secondary" onClick={() => setGridMetaData({ ...gridMetaData, page: (gridMetaData.page + 1) })}>Load More</Button>
                        }
                    </Box>
                </> : <>Nothing to show</>}
            </Box>
        </ThemeProvider>
    );
}

export default Milestones